import React from "react";
import {DataBaseComponent, DataBaseComponentState, Errors} from "components/DataBaseComponent";
import {BusyState} from "components/BusyFromPromise/BusyFromPromise";
import {Select} from "components/form";
import repository from "client/repository";
import SaveDialogLayout from "components/DialogLayout/SaveDialogLayout";
import InternalLink from "components/Navigation/InternalLink/InternalLink";
import ExternalLink from "components/Navigation/ExternalLink/ExternalLink";
import {instancesRouteLinks} from "areas/instances/instancesRouteLinks";
import {CollectDotnetTraceRequest} from "client/resources/collectDotnetTraceRequest";
import {EphemeralDebugTarget, ephemeralDebugTargetDescriptions} from "client/resources/ephemeralDebugTarget";

const durationOptions = [
    {value: "00:00:30", text: "30 seconds"},
    {value: "00:01:00", text: "1 minute"},
    {value: "00:02:00", text: "2 minutes"},
    {value: "00:05:00", text: "5 minutes"}
];

interface CollectDotnetTraceDialogProps {
    errors: Errors;
    busy: BusyState;
    instanceId: string;
    onSaveClick(backgroundTaskId: string): Promise<boolean>;
}

interface CollectDotnetTraceDialogState extends DataBaseComponentState {
    request: CollectDotnetTraceRequest;
}

export class CollectDotnetTraceDialog extends DataBaseComponent<CollectDotnetTraceDialogProps, CollectDotnetTraceDialogState> {
    constructor(props: CollectDotnetTraceDialogProps) {
        super(props);
        this.state = {
            request: {
                Duration: durationOptions[0].value,
                Target: EphemeralDebugTarget.OctopusServer
            }
        };
    }

    render() {
        const request = this.state.request;
        return <SaveDialogLayout title={"Collect .NET trace"}
                                 saveButtonLabel={"Collect"}
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 onSaveClick={() => this.collect()}>

            <Select value={request.Target}
                    onChange={(Target: EphemeralDebugTarget) => this.updateRequest({Target})}
                    items={Object.keys(EphemeralDebugTarget).map(t => ({value: t, text: ephemeralDebugTargetDescriptions[t as EphemeralDebugTarget]}))}
                    fieldName="Target"
            />

            <Select value={request.Duration}
                    onChange={Duration => this.updateRequest({Duration})}
                    items={durationOptions}
                    fieldName="Duration"
            />

            <br/>
            <div>
                A task will be created in the background to collect the trace.<br/>
                Once the <InternalLink to={instancesRouteLinks.instance(this.props.instanceId).tasks}>task</InternalLink> completes,
                a <code>.nettrace</code> file can be found <InternalLink to={instancesRouteLinks.instance(this.props.instanceId).diagnosticFiles}>here</InternalLink>.<br/>
                Download it and open it in Visual Studio, dotTrace, or convert it on your machine to view it as a flame graph:<br/>
                <code>dotnet-trace convert --format Speedscope &lt;file&gt;.nettrace</code><br/>
                then open the resulting <code>.speedscope.json</code> in <ExternalLink href="https://www.speedscope.app">speedscope.app</ExternalLink>.
            </div>
        </SaveDialogLayout>;
    }

    private updateRequest(update: Partial<CollectDotnetTraceRequest>) {
        return this.setState(prevState => ({request: {...prevState.request, ...update}}));
    }

    private collect() {
        return this.doBusyTask(async () => {
            const trace = await repository.HostedInstances.collectDotnetTrace(this.props.instanceId, this.state.request);
            await this.props.onSaveClick(trace.BackgroundTaskId);
            return true;
        });
    }
}
